import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Button,
  Space,
  Statistic,
  Alert,
  Modal,
  Tooltip,
  Select,
  InputNumber,
  Descriptions,
  Input,
} from "antd";
import {
  WarningOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ShoppingCartOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getShortageReport,
  runBatchMRP,
  generatePRFromShortage,
} from "../../api/mrpApi";
import { getAllJobs } from "../../api/jobApi";
import FulfillmentBadge from "./FulfillmentBadge";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Option } = Select;
const { TextArea } = Input;

const MRPDashboard = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [shortages, setShortages] = useState({
    total_shortages: 0,
    shortages: [],
    aggregated: [],
  });
  const [jobs, setJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);

  // PR generation modal
  const [prModalVisible, setPrModalVisible] = useState(false);
  const [selectedShortage, setSelectedShortage] = useState(null);
  const [prQuantity, setPrQuantity] = useState(0);
  const [prNotes, setPrNotes] = useState("");
  const [prSubmitting, setPrSubmitting] = useState(false);

  // Filters
  const [stageFilter, setStageFilter] = useState(undefined);

  // ── Fetch ──────────────────────────────────────────────────
  useEffect(() => {
    fetchData();
  }, []);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shortageData, jobsData] = await Promise.allSettled([
        getShortageReport(stageFilter ? { stage: stageFilter } : {}),
        getAllJobs(),
      ]);

      if (shortageData.status === "fulfilled") {
        setShortages(shortageData.value);
      }
      if (jobsData.status === "fulfilled") {
        setJobs(jobsData.value.jobs || []);
      }
    } catch (error) {
      toast.error("Failed to load MRP data");
    }
    setLoading(false);
  };

  // ── Batch MRP ──────────────────────────────────────────────
  const handleRunBatchMRP = async () => {
    if (selectedJobIds.length === 0) {
      toast.warning("Select one or more jobs to run MRP");
      return;
    }

    setBatchRunning(true);
    try {
      const result = await runBatchMRP(selectedJobIds);
      toast.success(
        `MRP completed: ${result.succeeded}/${result.total} succeeded`
      );
      if (result.failed > 0) {
        toast.warning(`${result.failed} job(s) failed`);
      }
      setSelectedJobIds([]);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Batch MRP failed"
      );
    }
    setBatchRunning(false);
  };

  // ── Generate PR ────────────────────────────────────────────
  const openPRModal = (record) => {
    setSelectedShortage(record);
    setPrQuantity(record.total_shortage || 0);
    setPrNotes("Auto-generated from MRP shortage");
    setPrModalVisible(true);
  };

  const handleGeneratePR = async () => {
    if (!selectedShortage) return;

    setPrSubmitting(true);
    try {
      await generatePRFromShortage({
        material_id: selectedShortage.material_id,
        quantity: prQuantity,
        unit: selectedShortage.unit,
        job_ids: selectedShortage.affected_jobs?.map((j) => j.job_id) || [],
        notes: prNotes,
      });
      toast.success("Purchase Request created successfully");
      setPrModalVisible(false);
      setSelectedShortage(null);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create Purchase Request"
      );
    }
    setPrSubmitting(false);
  };

  // ── Stats ──────────────────────────────────────────────────
  const summaryStats = {
    total: jobs.length,
    fulfilled: jobs.filter((j) => j.fulfillment_status === "fulfilled").length,
    partial: jobs.filter((j) => j.fulfillment_status === "partial").length,
    critical: jobs.filter(
      (j) =>
        j.fulfillment_status === "critical" ||
        j.fulfillment_status === "unfulfilled"
    ).length,
  };

  // ── Table: Jobs ────────────────────────────────────────────
  const jobColumns = [
    {
      title: "Job #",
      dataIndex: "job_number",
      key: "job_number",
      render: (text, record) => (
        <Link to={`/job/${record.id}`}>{text}</Link>
      ),
      sorter: (a, b) => (a.job_number || "").localeCompare(b.job_number || ""),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      ellipsis: true,
    },
    {
      title: "Qty",
      dataIndex: "production_quantity",
      key: "production_quantity",
      render: (qty) => qty?.toLocaleString(),
      width: 90,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => {
        const colors = {
          urgent: "red",
          high: "orange",
          normal: "blue",
          low: "default",
        };
        return priority ? (
          <Tag color={colors[priority] || "default"}>
            {priority.toUpperCase()}
          </Tag>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Stage",
      key: "stage",
      width: 120,
      render: (_, record) =>
        record.current_stage_name ? (
          <Tag color={record.stage_color}>{record.current_stage_name}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Material Status",
      dataIndex: "fulfillment_status",
      key: "fulfillment_status",
      width: 140,
      render: (status) => <FulfillmentBadge status={status} />,
      filters: [
        { text: "Fulfilled", value: "fulfilled" },
        { text: "Partial", value: "partial" },
        { text: "Unfulfilled", value: "unfulfilled" },
      ],
      onFilter: (value, record) => record.fulfillment_status === value,
    },
    {
      title: "Delivery",
      dataIndex: "expected_delivery",
      key: "expected_delivery",
      width: 110,
      render: (date) =>
        date ? new Date(date).toLocaleDateString() : "-",
      sorter: (a, b) =>
        new Date(a.expected_delivery || 0) -
        new Date(b.expected_delivery || 0),
    },
  ];

  // ── Table: Shortages ───────────────────────────────────────
  const shortageColumns = [
    {
      title: "Material",
      key: "material",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.material?.name || "Unknown"}
          </div>
          <small style={{ color: "#888" }}>
            {record.material?.code}
          </small>
        </div>
      ),
    },
    {
      title: "Category",
      key: "category",
      width: 90,
      render: (_, record) => (
        <Tag>{record.material_type || record.material?.category}</Tag>
      ),
    },
    {
      title: "Shortage",
      dataIndex: "total_shortage",
      key: "total_shortage",
      width: 120,
      render: (qty, record) => (
        <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
          {typeof qty === "number" ? qty.toFixed(2) : qty}{" "}
          {record.unit || record.material?.unit}
        </span>
      ),
      sorter: (a, b) => (a.total_shortage || 0) - (b.total_shortage || 0),
    },
    {
      title: "Affected Jobs",
      dataIndex: "affected_jobs",
      key: "affected_jobs",
      width: 110,
      render: (affectedJobs) => (
        <Tooltip
          title={affectedJobs?.map((j) => j.job_number).join(", ")}
        >
          <Tag color="red">{affectedJobs?.length || 0} job(s)</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<ShoppingCartOutlined />}
          onClick={() => openPRModal(record)}
        >
          Create PR
        </Button>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <div>
      <PageTitle title="Back" subtitle="MRP Planning" />

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Total Jobs"
              value={summaryStats.total}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Fulfilled"
              value={summaryStats.fulfilled}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Partial"
              value={summaryStats.partial}
              valueStyle={{ color: "#faad14" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Critical / Unfulfilled"
              value={summaryStats.critical}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Shortages Alert */}
      {shortages.aggregated?.length > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message={`${shortages.aggregated.length} material(s) have shortages affecting ${shortages.total_shortages} job requirement(s)`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={16}>
        {/* Jobs Table */}
        <Col xs={24} lg={14}>
          <Card
            title="Jobs — Material Status"
            extra={
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchData}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={handleRunBatchMRP}
                  disabled={selectedJobIds.length === 0}
                  loading={batchRunning}
                >
                  Run MRP ({selectedJobIds.length})
                </Button>
              </Space>
            }
          >
            <Table
              columns={jobColumns}
              dataSource={jobs}
              rowKey="id"
              loading={loading}
              rowSelection={{
                selectedRowKeys: selectedJobIds,
                onChange: setSelectedJobIds,
              }}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
              scroll={{ x: 700 }}
            />
          </Card>
        </Col>

        {/* Shortages Table */}
        <Col xs={24} lg={10}>
          <Card
            title="Material Shortages"
            extra={
              <Select
                allowClear
                placeholder="Filter by stage"
                style={{ width: 160 }}
                value={stageFilter}
                onChange={(val) => {
                  setStageFilter(val);
                  // re-fetch with filter on next render
                }}
              >
                <Option value="corrugation">Corrugation</Option>
                <Option value="sheet_cutting">Sheet Cutting</Option>
                <Option value="pasting">Pasting</Option>
                <Option value="printing">Printing</Option>
                <Option value="casting">Casting</Option>
                <Option value="joining">Joining</Option>
              </Select>
            }
          >
            <Table
              columns={shortageColumns}
              dataSource={shortages.aggregated}
              rowKey={(record) => record.material_id || Math.random()}
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ x: 500 }}
              locale={{ emptyText: "No shortages — all materials fulfilled ✅" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Generate PR Modal */}
      <Modal
        title="Generate Purchase Requisition"
        open={prModalVisible}
        onOk={handleGeneratePR}
        onCancel={() => {
          setPrModalVisible(false);
          setSelectedShortage(null);
        }}
        okText="Create PR"
        confirmLoading={prSubmitting}
      >
        {selectedShortage && (
          <div>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Material">
                {selectedShortage.material?.name} ({selectedShortage.material?.code})
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {selectedShortage.material_type || selectedShortage.material?.category}
              </Descriptions.Item>
              <Descriptions.Item label="Total Shortage">
                <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                  {selectedShortage.total_shortage}{" "}
                  {selectedShortage.unit || selectedShortage.material?.unit}
                </span>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 500 }}>Order Quantity:</label>
              <InputNumber
                min={0}
                value={prQuantity}
                onChange={setPrQuantity}
                style={{ width: "100%", marginTop: 4 }}
                addonAfter={selectedShortage.unit || selectedShortage.material?.unit}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 500 }}>Notes:</label>
              <TextArea
                rows={2}
                value={prNotes}
                onChange={(e) => setPrNotes(e.target.value)}
                style={{ marginTop: 4 }}
              />
            </div>

            <div>
              <label style={{ fontWeight: 500 }}>Affected Jobs:</label>
              <ul style={{ marginTop: 4 }}>
                {selectedShortage.affected_jobs?.map((job) => (
                  <li key={job.job_id}>
                    <Link to={`/job/${job.job_id}`}>{job.job_number}</Link>
                    {" — "}
                    <span style={{ color: "#ff4d4f" }}>
                      {job.shortage_qty}{" "}
                      {selectedShortage.unit || selectedShortage.material?.unit}
                    </span>
                    {job.priority === "urgent" && (
                      <Tag color="red" style={{ marginLeft: 8 }}>
                        URGENT
                      </Tag>
                    )}
                    {job.priority === "high" && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        HIGH
                      </Tag>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MRPDashboard;
